<template>
  <div class="box">
    <BaseModal id="new-payment" v-model="showNewPaymentModal">
      <template v-slot:header>New Payment</template>
      <div class="w-full pt-4 p-4">
        <PaymentForm
          v-if="!creatingPayment"
          :amount="pendingAmount"
          :show-cardholder-field="true"
          :show-card-field="true"
          :show-amount-field="true"
          :submitting="creatingPayment"
          submit-text="Add Payment"
          submitting-text="Adding..."
          @submit="createPayment"
          @cancel="closeModal"
        />
        <BaseLoading :loading="creatingPayment"></BaseLoading>
      </div>
    </BaseModal>

    <!-- Stripe Payment Modal -->
    <StripePaymentModal
      v-if="showStripeModal && isStripeEnabled && !isDraft"
      :show="showStripeModal"
      :order-number="orderNumber"
      :amount="pendingAmount"
      @close="showStripeModal = false"
      @payment-success="handleStripePaymentSuccess"
    />

    <!-- Detalle en vivo desde Stripe + reembolsos -->
    <StripePaymentDetailModal
      v-if="selectedStripePaymentIntentId && canRefundPayment"
      :show="!!selectedStripePaymentIntentId"
      :order-number="orderNumber"
      :payment-intent-id="selectedStripePaymentIntentId"
      @close="selectedStripePaymentIntentId = null"
      @refunded="$emit('refundedPayment', $event)"
    />

    <!-- Placetopay (Costa Rica): detalle y reembolsos -->
    <PlacetopayPaymentDetailModal
      v-if="selectedPlacetopayPaymentId && canRefundPayment"
      :show="!!selectedPlacetopayPaymentId"
      :order-number="orderNumber"
      :payment-id="selectedPlacetopayPaymentId"
      @close="selectedPlacetopayPaymentId = null"
      @refunded="$emit('refundedPayment', $event)"
    />

    <div class="box-header">
      Payments
    </div>
    <div class="content">
      <div class="table-full" v-if="payments.length > 0">
        <table class="default-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Payments Processor</th>
              <th>Payment id / Transaction</th>
              <th>Cardholder</th>
              <th>Card</th>
              <th>amount</th>
              <th>date</th>
              <th></th>
            </tr>
          </thead>
          <tr v-for="payment in payments" :key="payment.id">
            <td
              data-label="Status"
              :class="{ 'bg-red-400 text-white': payment.statusSuccess == '0' }"
            >
              {{ payment.statusSuccess == "0" ? "Denied" : "Approved" }}
            </td>
            <td data-label="Payments Processor">
              {{ payment.paymentType }}
            </td>
            <td
              data-label="Payment id / Transaction"
              class="payment-transaction"
              style="text-align: center"
            >
              {{
                [payment.transactionId, payment.transactionTag]
                  .filter(Boolean)
                  .join("-")
              }}
              <template v-if="payment.paypalPayerEmail">
                <br />
                {{ payment.paypalPayerEmail }}
              </template>
              <template v-if="payment.paypalPayerId">
                <br />
                {{ payment.paypalPayerId }}
              </template>
            </td>
            <td
              data-label="Cardholder"
              :class="{ 'is-empty': !payment.cardholderName }"
            >
              {{ payment.cardholderName }}
            </td>
            <td data-label="Card" :class="{ 'is-empty': !payment.card }">
              {{ payment.card }}
            </td>
            <td
              data-label="Amount"
              :class="{ 'text-red-600 font-semibold': payment.amount < 0 }"
            >
              {{ payment.amount }}
            </td>
            <td data-label="Date">
              {{ payment.dateTransaction | dateWithHours }}
            </td>
            <td class="payment-actions whitespace-nowrap">
              <button
                v-if="canRefundPayment && isStripePayment(payment)"
                type="button"
                class="inline-flex items-center justify-center w-8 h-8 mr-1 align-middle rounded-md text-2xl text-blue-600 hover:bg-blue-50"
                title="View"
                aria-label="View"
                @click="selectedStripePaymentIntentId = payment.transactionId"
              >
                <i class="mdi mdi-eye-outline"></i>
              </button>
              <button
                v-if="canRefundPayment && isPlacetopayPayment(payment)"
                type="button"
                class="inline-flex items-center justify-center w-8 h-8 mr-1 align-middle rounded-md text-2xl text-blue-600 hover:bg-blue-50"
                title="View / Refund"
                aria-label="View / Refund"
                @click="selectedPlacetopayPaymentId = payment.id"
              >
                <i class="mdi mdi-eye-outline"></i>
              </button>
              <button
                type="button"
                class="inline-flex items-center justify-center w-8 h-8 align-middle rounded-md hover:bg-red-50"
                title="Delete"
                aria-label="Delete"
                @click="deletePayment(payment.id)"
              >
                <img class="w-6" src="@/assets/icons/trash.svg" />
              </button>
            </td>
          </tr>
        </table>
      </div>
      <div v-if="payments.length === 0" class="text-red-500">
        This order does not have any payment.
      </div>
      <div v-if="isDraft" class="text-red-500">
        This order is in draft, confirm to pay.
      </div>
      <div
        v-if="canAddPayment && !isDraft"
        class="w-full flex justify-end gap-2"
      >
        <button
          class="btn-success px-4 py-2 rounded-md font-semibold"
          @click="showStripeModal = true"
          v-if="pendingAmount > 0 && isStripeEnabled"
        >
          💳 Stripe Payment
        </button>
        <button class="btn-dark-button w-12 h-12" @click="startCreatePayment()">
          +
        </button>
      </div>
      <div v-if="!canAddPayment && !isDraft" class="text-red-500">
        Adding payments is not allowed for this order. If this order has a
        parent order, you can add payments to the parent order.
      </div>
      <button
        type="button"
        v-if="!showLogs && paymentLogs.length > 0"
        @click="showLogs = true"
        class="text-base"
      >
        Show logs
      </button>
      <button
        type="button"
        v-if="showLogs"
        @click="showLogs = false"
        class="text-base"
      >
        Hide logs
      </button>
      <div class="w-full" v-if="showLogs">
        <ul>
          <li v-for="log in paymentLogs" :key="log.id" class="my-1 border-b">
            {{ log.time }}: <b>{{ log.response }}</b>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
<script>
import {
  deletePayment as deletePaymentService,
  addPaymentToOrder
} from "@/api/store/OrdersService.js";
import PaymentForm from "./PaymentForm.vue";
import StripePaymentModal from "./StripePaymentModal.vue";
import StripePaymentDetailModal from "./StripePaymentDetailModal.vue";
import PlacetopayPaymentDetailModal from "./PlacetopayPaymentDetailModal.vue";

export default {
  components: {
    PaymentForm,
    StripePaymentModal,
    StripePaymentDetailModal,
    PlacetopayPaymentDetailModal
  },
  props: {
    payments: {
      type: Array,
      required: true
    },
    orderNumber: {
      type: Number,
      required: true
    },
    paymentLogs: {
      type: Array,
      required: false
    },
    canAddPayment: {
      type: Boolean,
      required: false,
      default: true
    },
    pendingAmount: {
      type: Number,
      required: false,
      default: 0
    },
    orderStatus: {
      type: String,
      required: false,
      default: ""
    }
  },
  data() {
    return {
      showLogs: false,
      showNewPaymentModal: false,
      showStripeModal: false,
      creatingPayment: false,
      selectedStripePaymentIntentId: null,
      selectedPlacetopayPaymentId: null
    };
  },
  computed: {
    isDraft() {
      return this.orderStatus === "Draft";
    },
    getAdminSettings() {
      return this.$store.getters["general/getAdminSettings"];
    },
    canRefundPayment() {
      return this.$store.getters["auth/userHasPermission"](
        "can_refund_payment"
      );
    },
    isStripeEnabled() {
      return this.getAdminSettings.get("payment_stripe")?.value === "true";
    }
  },
  methods: {
    // Pagos cobrados con Stripe (orden o POS admin): el transaction_id es el
    // PaymentIntent. Las filas de reembolso ("Stripe Refund", re_...) no aplican.
    isStripePayment(payment) {
      return (
        String(payment.paymentType || "").toLowerCase() === "stripe" &&
        String(payment.transactionId || "").startsWith("pi_") &&
        Number(payment.amount) > 0
      );
    },
    // Pagos cobrados con Placetopay (tienda CR). Las filas "Placetopay Refund" no aplican.
    isPlacetopayPayment(payment) {
      return payment.paymentType === "Placetopay" && Number(payment.amount) > 0;
    },
    startCreatePayment() {
      if (this.isDraft) {
        this.$toast.open({
          message: "This order is in draft, confirm to pay.",
          type: "warning",
          position: "top-right"
        });
        return;
      }
      this.showNewPaymentModal = true;
    },
    closeModal() {
      this.showNewPaymentModal = false;
    },
    deletePayment(id) {
      if (confirm("Are you sure you want to delete this payment")) {
        deletePaymentService(id, this.orderNumber)
          .then(() => {
            this.$emit("deletedPayment", id);
            this.$toast.open({
              message: `Payment deleted`,
              type: "success",
              position: "top-right"
            });
          })
          .catch(err => {
            this.$toast.open({
              message: `${err.response.data ?? err.message}`,
              type: "error",
              position: "top-right"
            });
          });
      }
    },
    createPayment(paymentData) {
      if (this.isDraft) {
        this.$toast.open({
          message: "This order is in draft, confirm to pay.",
          type: "warning",
          position: "top-right"
        });
        return;
      }
      this.creatingPayment = true;
      addPaymentToOrder(this.orderNumber, paymentData)
        .then(data => {
          this.$emit("createdPayment", paymentData);
          this.$toast.open({
            message: `Payment created`,
            type: "success",
            position: "top-right"
          });
          paymentData.id = data;
          this.creatingPayment = false;
          this.showNewPaymentModal = false;
          console.log(paymentData);
        })
        .catch(err => {
          this.$toast.open({
            message: `${err.response.data ?? err.message}`,
            type: "error",
            position: "top-right"
          });
          this.creatingPayment = false;
        });
    },
    handleStripePaymentSuccess(paymentData) {
      this.showStripeModal = false;
      this.$emit("createdPayment", paymentData);
      this.$toast.open({
        message: "Stripe payment processed successfully!",
        type: "success",
        position: "top-right"
      });
    }
  }
};
</script>
<style lang="scss">
// Sin scoped: .modal-content vive en BaseModal. Dentro del modal de orden
// .order-modal .modal-content fuerza ancho/alto completos a los modales
// anidados; el id le gana en especificidad.
#new-payment > .modal-content {
  width: 100%;
  max-width: 800px;
  height: auto;
  // .modal ya deja 100px arriba; 40px de aire abajo
  max-height: calc(100vh - 140px);
  margin: 0 auto;
  overflow-y: auto;

  @media (max-width: 767px) {
    max-width: 100%;
    max-height: 100vh;
    max-height: 100dvh;
    margin: 0;
    border-radius: 0;
  }
}
</style>
